import type { GLPrecision, GLProbeVersion, WebGLCapabilities } from '../types';

/**
 * Base class for WebGL capability probing.
 *
 * Implementations should:
 * - attempt to obtain a WebGL context (browser impl)
 * - populate fields (precision, maxTextureSize)
 * - provide a DTO via getCapabilities()
 */
export abstract class GLProbe {
  /**
   * Best supported fragment-float precision detected by the probe.
   * `undefined` means probing hasn't been executed yet or WebGL is unavailable.
   */
  protected precision?: GLPrecision;

  protected version?: GLProbeVersion;

  /**
   * Maximum supported texture size reported by WebGL (gl.MAX_TEXTURE_SIZE).
   * `undefined` means probing hasn't been executed yet or WebGL is unavailable.
   */
  protected maxTextureSize?: number;

  /**
   * Performs environment-specific WebGL probing.
   *
   * @param canvas Optional canvas used to create a WebGL context (browser impl).
   *              In Node.js implementation it is ignored.
   */
  abstract queryWebGL(canvas?: HTMLCanvasElement): void;

  /**
   * Checks whether the environment supports textures of at least the given size.
   *
   * @param textureSize Required texture size (e.g. 4096).
   */
  abstract isSupported(textureSize: number): boolean;

  /**
   * Returns a normalized snapshot of detected WebGL capabilities.
   *
   * @param textureRequirement Minimum texture size requirement used to compute `supported`.
   */
  getCapabilities(textureRequirement: number): WebGLCapabilities {
    return {
      supported: this.isSupported(textureRequirement),
      version: this.version,
      maxTextureSize: this.maxTextureSize,
      precision: this.precision,
    };
  }
}
